import React, { useState } from 'react'
import {
  Flex,
  H2,
  Anchor,
  Button,
  ContactUsModal,
  BusinessContactModal,
} from '../../lib/components'
import { useBreakpoint } from '../../lib/utils/hooks'
import { useNavigate } from 'react-router-dom'
import './about-us.scss'
import { MD } from '../../lib/utils/constants'

const AboutUs = () => {
  const { breakpoint } = useBreakpoint()
  const [selectedFounder, setSelectedFounder] = useState<string | null>(null)
  const [showContactUsModal, setShowContactUsModal] = useState(false)
  const [showBusinessContactModal, setShowBusinessContactModal] = useState(false)
  const navigate = useNavigate()

  return (
    <>
      {/* About us cover section */}
      <div className='about-us-hero-section'>
        <Flex variant='column' classes={['about-us-hero-content']}>
          <H2 classes={['mb-24']} text='Investing Where Global Momentum Begins' />
          <p className='mb-24'>
            Credibila provides regulated, transparent access to curated private market opportunities
            worldwide.
          </p>
          <Anchor
            href='/opportunities'
            text='Explore Opportunities →'
            variant='basic-anchor'
            classes={['about-us-cta-blue']}
          />
        </Flex>

        <img
          src='https://credibila-assets.s3.amazonaws.com/images/aboutus_hero.png'
          alt='about cover'
          className='about-us-hero-image'
        />
      </div>

      {/* Private markets section */}
      <div className='about-us-wrapper-bravo'>
        <div className='about-us-private-container'>
          <Flex
            classes={['about-us-content-container-bravo', 'gap-80', 'flex-align-center']}
            dataTestId='about-us-private-markets'
          >
            <Flex variant='column' classes={['about-us-content', 'about-us-column-left']}>
              <H2 text="Private markets weren't built for exclusivity; they drifted into it over time." />
            </Flex>
            <Flex variant='column' classes={['about-us-content', 'about-us-column-right']}>
              <p className='basic regular'>
                Credibila allows you to access private markets with a low minimum of US $ 1000.
              </p>
              <Anchor
                href='/faq'
                text="Learn How We're Opening Access"
                variant='basic-anchor'
                classes={['about-us-cta-golden', 'mt-24']}
              />
            </Flex>
          </Flex>
        </div>
      </div>

      {/* Regulated bridge section */}
      <div className='about-us-wrapper-alpha'>
        <div className='about-us-regulated-container'>
          <Flex
            classes={['about-us-content-container-alpha', 'gap-80', 'flex-align-center']}
            dataTestId='about-us-regulated-bridge'
          >
            <Flex variant='column' classes={['about-us-content', 'about-us-column-left']}>
              <p className='basic regular'>
                Connecting global clients to regulated, institution-grade private market
                opportunities through a single, compliant platform.
              </p>
            </Flex>
            <Flex variant='column' classes={['about-us-content', 'about-us-column-right']}>
              <H2 text="A regulated bridge to the world's most compelling private opportunities." />
            </Flex>
          </Flex>
        </div>
      </div>

      {/* Built for trust section */}
      <div className='about-us-trust-section'>
        <h2 className='about-us-trust-heading'>Designed for access. Built for trust.</h2>
        <Flex
          classes={[
            'about-us-trust-container',
            `${breakpoint && breakpoint <= MD ? 'column' : ''}`,
          ]}
          dataTestId='about-us-trust'
        >
          <div className='about-us-trust-card'>
            <div className='about-us-trust-icon'>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h3 className='about-us-trust-card-title'>Evaluate & Approve</h3>
            <p className='about-us-trust-card-text'>
              Each opportunity is independently vetted by our diligence and advisory partners; only
              approved opportunities proceed to clients.
            </p>
          </div>

          <div className='about-us-trust-card'>
            <div className='about-us-trust-icon'>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                <circle
                  cx='11'
                  cy='11'
                  r='8'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M21 21l-4.35-4.35'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h3 className='about-us-trust-card-title'>Review & Invest</h3>
            <p className='about-us-trust-card-text'>
              Approved deals include structured data rooms and key metrics for client analysis;
              investments are completed digitally via secure payment and custody channels.
            </p>
          </div>

          <div className='about-us-trust-card'>
            <div className='about-us-trust-icon'>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                <rect
                  x='3'
                  y='3'
                  width='18'
                  height='18'
                  rx='2'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M9 3v18'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h3 className='about-us-trust-card-title'>Monitor & Report</h3>
            <p className='about-us-trust-card-text'>
              Clients receive periodic updates and clear performance tracking through transparent
              reporting and custodial safeguards.
            </p>
          </div>
        </Flex>
      </div>

      {/* Credibila in Numbers section */}
      <div className='about-us-numbers-section'>
        <h2 className='about-us-numbers-heading'>Credibila in Numbers.</h2>
        <Flex
          classes={[
            'about-us-numbers-container',
            `${breakpoint && breakpoint <= MD ? 'column' : ''}`,
          ]}
          dataTestId='about-us-numbers'
        >
          <div className='about-us-number-card'>
            <div className='about-us-number-value'>6+ countries</div>
            <div className='about-us-number-label'>Clients across the globe</div>
          </div>

          <div className='about-us-number-card'>
            <div className='about-us-number-value'>10–15% p.a.</div>
            <div className='about-us-number-label'>Average return range</div>
          </div>

          <div className='about-us-number-card'>
            <div className='about-us-number-value'>12+ opportunities</div>
            <div className='about-us-number-label'>Active deals available</div>
          </div>

          <div className='about-us-number-card'>
            <div className='about-us-number-value'>$23+ Million</div>
            <div className='about-us-number-label'>Total capital invested</div>
          </div>
        </Flex>
      </div>

      {/* Meet the Founders section */}
      <div className='about-us-founders-section'>
        <h2 className='about-us-founders-heading'>Meet the Founders</h2>
        <Flex
          classes={[
            'about-us-founders-container',
            `${breakpoint && breakpoint <= MD ? 'column' : ''}`,
          ]}
        >
          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('amardeep')}
            data-testid='founder-card-amardeep'
          >
            <img
              src='https://credibila-assets.s3.amazonaws.com/images/about-us-img1.png'
              alt='Amardeep Sharma'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Amardeep Sharma</div>
              <div className='about-us-founder-title'>Chairman</div>
            </div>
          </div>

          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('karan')}
            data-testid='founder-card-karan'
          >
            <img
              src='https://credibila-assets.s3.amazonaws.com/images/about-us-img2.png'
              alt='Karan Rai'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Karan Rai</div>
              <div className='about-us-founder-title'>CTO</div>
            </div>
          </div>

          <div
            className='about-us-founder-card'
            onClick={() => setSelectedFounder('jalpesh')}
            data-testid='founder-card-jalpesh'
          >
            <img
              src='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-jalpesh.png'
              alt='Dr. Jalpesh Mehta'
              className='about-us-founder-image'
            />
            <div className='about-us-founder-label'>
              <div className='about-us-founder-name'>Dr. Jalpesh Mehta</div>
              <div className='about-us-founder-title'>Advisor</div>
            </div>
          </div>
        </Flex>
      </div>

      {/* Founder Modal */}
      {selectedFounder && (
        <div className='about-us-modal-overlay' onClick={() => setSelectedFounder(null)}>
          <div className='about-us-modal' onClick={(e) => e.stopPropagation()}>
            <button
              className='about-us-modal-close'
              onClick={() => setSelectedFounder(null)}
              data-testid='modal-close'
            >
              ×
            </button>
            {selectedFounder === 'amardeep' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.amazonaws.com/images/about-us-img1.png'
                    alt='Amardeep Sharma'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Amardeep Sharma</strong> - Chairman
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() =>
                          window.open(
                            'https://www.linkedin.com/in/amardeep-sharma-b150926?originalSubdomain=ae',
                          )
                        }
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      An alumnus of the University of Oxford with more than two decades of
                      experience in capital raising, investment banking, asset management, M&A, fund
                      management, trade finance, structured products solutions, and renewable energy
                      consulting & advisory, Mr. Amardeep Sharma, was honoured at the 24th Edition
                      of the Asian Business and Social Forum: Greatest Brands and Leaders 2024 by
                      AsiaOne Magazine. A distinguished leader in the financial sector, known for
                      his strategic vision and expertise in investment management, Mr. Sharma has
                      been driving success by delivering innovative financial solutions and
                      fostering sustainable growth across various industries. In recognition of his
                      exemplary leadership, being a trusted partner for businesses and clients,
                      fostering long-term value creation through strategic investments and sound
                      financial planning, he was honored with the prestigious Global Indian of the
                      Year 2023-24 Award.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'karan' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.amazonaws.com/images/about-us-img2.png'
                    alt='Karan Rai'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Karan Rai</strong> - CTO
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() => window.open('https://www.linkedin.com/in/karanrai')}
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      Mr. Karan Rai, an alumnus of the University of Oxford, brings over a decade of
                      experience in technology entrepreneurship and product development. As the
                      founder and CEO of Launch Ventures, he has been instrumental in creating
                      high-performance teams that deliver innovative technology solutions for
                      startups and established enterprises. Known for his commitment to addressing
                      global challenges, Karan has led initiatives focused on climate change and
                      misinformation. His leadership is characterized by a collaborative approach
                      that empowers teams to build meaningful products, positioning Launch Ventures
                      as a key player in the technology landscape.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
            {selectedFounder === 'jalpesh' && (
              <div className='about-us-modal-content'>
                <Flex classes={['gap-60', 'flex-align-center']}>
                  <img
                    src='https://credibila-assets.s3.ap-south-1.amazonaws.com/images-v2/about-us-img-jalpesh.png'
                    alt='Dr. Jalpesh Mehta'
                    className='about-us-modal-image'
                  />
                  <div>
                    <Flex classes={['flex-align-center', 'mb-24']}>
                      <h3 className='about-us-modal-title'>
                        <strong>Dr. Jalpesh Mehta</strong> - Advisor
                      </h3>
                      <img
                        src='https://credibila-assets.s3.amazonaws.com/opportunity-images/team-linkedIn.png'
                        alt='linkedin'
                        className='about-us-linkedin'
                        onClick={() =>
                          window.open('https://www.linkedin.com/in/jalpeshmehta/', '_blank')
                        }
                      />
                    </Flex>
                    <p className='about-us-modal-text'>
                      Dr. Jalpesh Mehta holds an MBA and a Ph.D. in Management Studies (Strategy)
                      from NMIMS University, Mumbai. He has over 25 years of experience across
                      corporate banking, private banking, NRI services, retail assets, and consumer
                      banking. He has held senior leadership roles at ICICI Bank, India, and has
                      been instrumental in setting up the NRI and international business in 11
                      countries across the Middle East, Asia Pacific, and Europe. As the chairperson
                      of IBAF (India Business Advisory Forum) and Mentor, AIM, NITI Aayog,
                      Government of India, Dr. Mehta has been inspiring entrepreneurs and helping
                      scale businesses.
                    </p>
                  </div>
                </Flex>
              </div>
            )}
          </div>
        </div>
      )}

      {/* For Clients & For Businesses Section */}
      <div className='about-us-cards-section'>
        <Flex
          classes={[
            'about-us-cards-container',
            `${breakpoint && breakpoint <= MD ? 'column' : ''}`,
          ]}
        >
          <div className='about-us-card'>
            <div className='about-us-card-gradient'></div>
            <h3 className='about-us-card-heading'>For Clients</h3>
            <p className='about-us-card-text'>
              Invest confidently in the world&apos;s private markets. Access diversified
              opportunities across asset classes and regions built for transparency and trust.
            </p>
            <Button
              text='Create account'
              variant='basic-btn'
              handleClick={() => navigate('/signup')}
              classes={['about-us-card-button']}
            />
          </div>

          <div className='about-us-card'>
            <div className='about-us-card-gradient'></div>
            <h3 className='about-us-card-heading'>For Businesses</h3>
            <p className='about-us-card-text'>
              Raise global capital through a regulated channel. Partner with Credibila to reach
              clients across borders & secure smarter, faster growth capital.
            </p>
            <Button
              text='Get in touch'
              variant='basic-btn'
              handleClick={() => setShowBusinessContactModal(true)}
              classes={['about-us-card-button']}
            />
          </div>
        </Flex>
      </div>

      {/* Leadership Section */}
      <div className='about-us-leadership-section'>
        <div className='about-us-leadership-content'>
          <h2 className='about-us-leadership-heading'>
            Leadership that unites finance, regulation, and innovation.
          </h2>
          <p className='about-us-leadership-text'>
            Credibila is led by a global team of financial experts, legal advisors, and
            technologists who believe private markets should be as transparent as public ones. Our
            leadership combines decades of experience in banking, asset management, and fintech
            innovation.
          </p>
        </div>
      </div>

      {/* Future Section */}
      <div className='about-us-future-section'>
        <div className='about-us-future-content'>
          <h2 className='about-us-future-heading'>
            The future of private investing is transparent, trusted, and borderless.
          </h2>
          <p className='about-us-future-text'>
            At Credibila, we&apos;re not just building a platform, we&apos;re shaping a more open
            financial ecosystem where clients everywhere can access, understand, and participate in
            the opportunities that move the world forward.
          </p>
          <div className='about-us-future-button-wrapper'>
            <Button
              text='Explore the Marketplace'
              variant='basic-btn'
              handleClick={() => navigate('/opportunities')}
              classes={['about-us-future-button']}
            />
          </div>
        </div>
      </div>

      {showContactUsModal && (
        <ContactUsModal
          active={showContactUsModal}
          handleOnClose={() => setShowContactUsModal(false)}
        />
      )}

      {showBusinessContactModal && (
        <BusinessContactModal
          active={showBusinessContactModal}
          handleOnClose={() => setShowBusinessContactModal(false)}
        />
      )}
    </>
  )
}

export { AboutUs }
